/**
 * /transport/apply — public transporter application form
 * --------------------------------------------------------
 * Single-form (no multi-step) intake for transporter applications.
 * Submits to POST /api/transport/apply, which admin reviews in
 * /admin/transporters → Applications tab. On approval, the applicant
 * receives a Stripe Connect onboarding link via email.
 *
 * File uploads (USDA license, insurance certificate, rig photo,
 * kennel photo) are URL inputs — applicants paste a public Google
 * Drive / Dropbox / Imgur link. This keeps the endpoint cheap and
 * makes uploads independently retryable. A signed-upload UX could
 * replace this in Phase 4 if friction is high.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { TruckIcon as Truck, ShieldCheckIcon as ShieldCheck, FileTextIcon as FileText, ArrowRightIcon as ArrowRight, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import DateOfBirthInput from "@/components/DateOfBirthInput";

const BLANK = {
  display_name: "",
  legal_name: "",
  email: "",
  phone: "",
  usda_license_number: "",
  usda_license_expiry: "",
  usda_license_url: "",
  insurance_carrier: "",
  insurance_policy_number: "",
  insurance_expiry: "",
  insurance_liability_usd: 300000,
  insurance_certificate_url: "",
  rig_photo_url: "",
  kennel_photo_url: "",
  service_area_states: "",
  accepted_agreement: false,
  extra_notes: "",
};

export default function TransportApply() {
  const navigate = useNavigate();
  const [form, setForm] = useState(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.accepted_agreement) {
      toast.error("Please accept the Transporter Agreement to apply.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        legal_name: form.legal_name || null,
        phone: form.phone || null,
        usda_license_expiry: form.usda_license_expiry || null,
        insurance_expiry: form.insurance_expiry || null,
        insurance_liability_usd: Number(form.insurance_liability_usd) || 300000,
        rig_photo_url: form.rig_photo_url || null,
        kennel_photo_url: form.kennel_photo_url || null,
        service_area_states: form.service_area_states
          .split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
        extra_notes: form.extra_notes || null,
      };
      const res = await api.post("/transport/apply", payload);
      setSubmitted(res.data.application);
      toast.success("Application submitted", {
        description: "Check your email — we'll review within 48 business hours.",
      });
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="application-success">
          <CheckCircle size={48} weight="duotone" className="text-emerald-600 mx-auto mb-3" />
          <h1 className="font-spectral text-3xl">Application received</h1>
          <p className="text-[14px] text-[#6B6B6B] mt-2 max-w-md mx-auto leading-relaxed">
            Thanks, {submitted.display_name}. We'll review your USDA license
            and insurance certificate and respond within{" "}
            <strong>48 business hours</strong>.
          </p>
          <p className="text-[11px] text-[#9A9A9A] mt-3 font-mono">
            Application ID: {submitted.id}
          </p>
          <div className="mt-6">
            <Button onClick={() => navigate("/")} className="rounded-full bg-ink text-white">
              Back to Armstrong K9 Registry
            </Button>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="transport-apply-page">
        <Link to="/" className="text-[12px] text-[#6B6B6B] hover:text-ink">
          ← Back to Armstrong K9 Registry
        </Link>
        <div className="flex items-center gap-3 mt-3 mb-1">
          <Truck size={32} weight="duotone" className="text-ink" />
          <h1 className="font-spectral text-4xl tracking-tight">
            Apply as a transporter
          </h1>
        </div>
        <p className="text-[14px] text-[#6B6B6B] leading-relaxed mb-3 max-w-2xl">
          Join Armstrong's verified transporter roster. 10% platform fee on
          completed trips. No subscription, no listing fee, escrowed payouts
          on every trip. Approval typically within 48 business hours.
        </p>
        <Link to="/transport/getting-started" className="text-[12px] font-semibold text-crimson hover:text-ink underline underline-offset-2 inline-block mb-6" data-testid="apply-page-walkthrough-link">
          Not sure how this works? Read the 8-step walkthrough →
        </Link>

        <div className="flex flex-wrap gap-3 mb-8 text-[12px] text-[#6B6B6B]">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#EAEAEA] bg-[#FAFAFA]">
            <ShieldCheck size={14} weight="duotone" className="text-emerald-600" />
            USDA Class III required
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#EAEAEA] bg-[#FAFAFA]">
            <FileText size={14} weight="duotone" className="text-ink" />
            $300K liability minimum
          </span>
        </div>

        <form onSubmit={onSubmit} className="space-y-6" data-testid="apply-form">
          {/* Business info */}
          <section className="space-y-3">
            <h2 className="font-spectral text-xl tracking-tight">Business info</h2>
            <Field label="Display name *" value={form.display_name}
              onChange={(v) => set("display_name", v)}
              placeholder="Bryson Family Hauling" required testId="apply-display-name" />
            <Field label="Legal / DBA name" value={form.legal_name}
              onChange={(v) => set("legal_name", v)}
              placeholder="Bryson Family Hauling, LLC" testId="apply-legal-name" />
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Email *" type="email" value={form.email}
                onChange={(v) => set("email", v)} required testId="apply-email" />
              <Field label="Phone" value={form.phone}
                onChange={(v) => set("phone", v)} placeholder="+1 ..." testId="apply-phone" />
            </div>
          </section>

          {/* USDA */}
          <section className="space-y-3">
            <h2 className="font-spectral text-xl tracking-tight">USDA license</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="License # *" value={form.usda_license_number}
                onChange={(v) => set("usda_license_number", v)}
                placeholder="43-B-0123" required testId="apply-usda-number" />
              <Field label="Expiry date" type="date" value={form.usda_license_expiry}
                onChange={(v) => set("usda_license_expiry", v)} testId="apply-usda-expiry" />
            </div>
            <Field label="Public link to license PDF / photo *"
              value={form.usda_license_url}
              onChange={(v) => set("usda_license_url", v)}
              placeholder="https://drive.google.com/... (must be publicly viewable)"
              required testId="apply-usda-url" />
          </section>

          {/* Insurance */}
          <section className="space-y-3">
            <h2 className="font-spectral text-xl tracking-tight">Insurance</h2>
            <Field label="Carrier *" value={form.insurance_carrier}
              onChange={(v) => set("insurance_carrier", v)}
              placeholder="Progressive Commercial" required testId="apply-ins-carrier" />
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Policy # *" value={form.insurance_policy_number}
                onChange={(v) => set("insurance_policy_number", v)}
                required testId="apply-ins-policy" />
              <Field label="Expiry" type="date" value={form.insurance_expiry}
                onChange={(v) => set("insurance_expiry", v)} testId="apply-ins-expiry" />
              <Field label="Liability $ *" type="number"
                value={form.insurance_liability_usd}
                onChange={(v) => set("insurance_liability_usd", v)}
                required testId="apply-ins-liability" />
            </div>
            <Field label="Public link to insurance certificate (COI) *"
              value={form.insurance_certificate_url}
              onChange={(v) => set("insurance_certificate_url", v)}
              placeholder="https://..." required testId="apply-ins-url" />
          </section>

          {/* Photos + service area */}
          <section className="space-y-3">
            <h2 className="font-spectral text-xl tracking-tight">Rig & service area</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Rig photo URL" value={form.rig_photo_url}
                onChange={(v) => set("rig_photo_url", v)}
                placeholder="https://..." testId="apply-rig-photo" />
              <Field label="Kennel/crate photo URL" value={form.kennel_photo_url}
                onChange={(v) => set("kennel_photo_url", v)}
                placeholder="https://..." testId="apply-kennel-photo" />
            </div>
            <Field label="Service area (comma-separated state codes)"
              value={form.service_area_states}
              onChange={(v) => set("service_area_states", v)}
              placeholder="TN, GA, AL, KY" testId="apply-states" />
          </section>

          {/* Notes */}
          <section>
            <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
              Anything else we should know? (Optional)
            </Label>
            <Textarea value={form.extra_notes}
              onChange={(e) => set("extra_notes", e.target.value)} rows={3}
              className="mt-1"
              placeholder="Preferred lanes, recurring breeders, special handling capabilities..."
              data-testid="apply-notes" />
          </section>

          {/* Agreement */}
          <section className="border border-[#E5E5E5] bg-[#FAFAFA] rounded-xl p-4">
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
              <a
                href="/api/transport/docs/agreement.pdf"
                target="_blank"
                rel="noreferrer"
                className="text-crimson font-semibold underline underline-offset-2 hover:text-ink"
                data-testid="apply-agreement-pdf-link"
              >
                Read the Transporter Agreement (PDF) →
              </a>
              <a
                href="/api/transport/docs/faq.pdf"
                target="_blank"
                rel="noreferrer"
                className="text-[#6B6B6B] underline underline-offset-2 hover:text-ink"
                data-testid="apply-faq-pdf-link"
              >
                Transporter FAQ (PDF)
              </a>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.accepted_agreement}
                onChange={(e) => set("accepted_agreement", e.target.checked)}
                className="mt-1"
                data-testid="apply-accept-agreement"
              />
              <span className="text-[13px] text-ink leading-relaxed">
                I have read and accept the{" "}
                <strong>Armstrong Transporter Agreement v1.0</strong>
                {" "}(Articles 1-11 covering relationship, eligibility, 10% platform fee,
                escrow, cancellation tiers, disputes, indemnification, and termination).
                I confirm I have authority to bind myself or the entity I represent,
                and I understand Armstrong is a listing service — not an insurer or
                broker.
              </span>
            </label>
          </section>

          <Button type="submit" disabled={submitting || !form.accepted_agreement}
            className="rounded-full bg-crimson hover:bg-ink text-white px-8 py-3"
            data-testid="apply-submit">
            {submitting ? "Submitting…" : "Submit application"} <ArrowRight size={14} className="ml-2" />
          </Button>
        </form>
      </div>
    </AppShell>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">{label}</Label>
      {type === "date" ? (
        <DateOfBirthInput
          value={value || ""}
          onChange={onChange}
          required={required}
          testId={testId}
          autoComplete="off"
          ariaLabel={label}
        />
      ) : (
        <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} required={required}
          className="mt-1 text-[13px]" data-testid={testId} />
      )}
    </div>
  );
}
